/* eslint-disable complexity */
import * as d3Scale from 'd3-scale';
import merge from 'deepmerge';
import { DvScatterData } from './type';
import { dataFormatter, getExtremeValue } from '../../util/data';
import * as graphicHandle from './graphic';
import type Chart from '../../../core/Chart';
import { isValidData } from './helper';
import { Option, DvScatterOption } from '../../../core/option';
import { getSeriesRelatedOption } from '../../util/option';
import { getCoordBoundingRect } from '../../util/coord';

const X_VALUE_INDEX = 0;
const Y_VALUE_INDEX = 1;
const R_VALUE_INDEX = 2;

export const axisTypeScaleMap = {
  category: d3Scale.scaleLinear,
  value: d3Scale.scaleLinear,
  time: d3Scale.scaleLinear,
  log: d3Scale.scaleLog
};

export class DvScatterModel {
  // Chart instance
  protected _chart: Chart;

  // Series index
  protected _index: number;

  // Current Data
  protected _data: DvScatterData[];

  // Series related option
  protected _option: Option;

  // common value
  protected _common = {
    xOffset: 0,
    yOffset: 0
  };

  constructor(chart: Chart, index: number, option: Option) {
    this._chart = chart;
    this._index = index;
    this._option = this._prepareOption(option);
    this._data = this._prepareData(this._option.series.data);
  }

  init(): void {
    this._parse();
  }

  protected _prepareOption(option: Option): Option {
    const relatedOption = getSeriesRelatedOption(this._index, option);
    const { series } = relatedOption;
    // formatter data
    series.data = dataFormatter(series.data);
    if (!series.dvRValueRange) {
      series.dvRValueRange = getExtremeValue(series.data, R_VALUE_INDEX);
    }

    const xAxisComponent = relatedOption.xAxis;
    const yAxisComponent = relatedOption.yAxis;
    if (!xAxisComponent.type) {
      xAxisComponent.type = 'value';
    }
    if (!yAxisComponent.type) {
      yAxisComponent.type = 'value';
    }

    if (xAxisComponent.type === 'log') {
      this._logAxisHandle('x', xAxisComponent);
    }

    if (yAxisComponent.type === 'log') {
      this._logAxisHandle('y', yAxisComponent);
    }

    return relatedOption;
  }

  /**
   * @rule if has log axis, the min value of axis should be greater than 0
   */
  protected _prepareData(data: DvScatterData[]): DvScatterData[] {
    const dataClone = merge([], data);
    // unified into object components
    // 偏移以适配对数轴
    const { xOffset, yOffset } = this._common;
    dataClone.forEach(d => {
      if (!d) {
        return;
      }
      if (d.value && d.value.length >= 3 && !d.value.find(v => isNaN(Number(v)))) {
        d.formatValue = [d.value[0] + xOffset, d.value[1] + yOffset];
      }
      if (d.nextValue && d.nextValue.length >= 2 && !d.nextValue.find(v => isNaN(Number(v)))) {
        d.formatNextValue = [d.nextValue[0] + xOffset, d.nextValue[1] + yOffset];
      }
      if (d.preValue && d.preValue.length >= 2 && !d.preValue.find(v => isNaN(Number(v)))) {
        d.formatPrevValue = [d.preValue[0] + xOffset, d.preValue[1] + yOffset];
      }
    });

    // 预处理图形层级
    const formatRValue = dataClone
      .filter(d => d && d.formatValue)
      .map(d => d.formatValue[R_VALUE_INDEX]);
    formatRValue.sort((a, b) => b - a);

    dataClone.forEach(d => {
      if (!d) {
        return;
      }
      if (!d.z) {
        d.z = formatRValue.indexOf(d.formatValue[R_VALUE_INDEX]);
      }
    });

    return dataClone;
  }

  protected _parse(): void {
    this._parseSeries();
    this._parseAxisPointer();
  }

  protected _parseSeries(): void {
    this._autoLabelLayout();
    this._renderItemHandle();
  }

  protected _parseAxisPointer(): void {
    this.dvEnableXOffsetHandle();
  }

  protected dvEnableXOffsetHandle(): void {
    const { axisPointer, xAxis } = this._option;
    if (!axisPointer || !axisPointer.label || !axisPointer.label.dvEnableXOffset) {
      return;
    }

    const labelOption = axisPointer.label;
    const dom = this._chart.getDom();
    const gridBoundingRect = getCoordBoundingRect(dom as HTMLDivElement, this._option.grid || {});
    const ECHARTS_DEFAULT = {
      margin: 3,
      fontSize: 12,
      padding: [5, 7, 5, 7]
    };
    const finalMargin = labelOption.margin || ECHARTS_DEFAULT.margin;
    const finalFontSize = labelOption.fontSize || ECHARTS_DEFAULT.fontSize;
    const finalPadding = labelOption.padding || ECHARTS_DEFAULT.padding;
    const offset = finalMargin * 2 + finalFontSize + finalPadding[0] + finalPadding[2];

    xAxis.axisPointer = merge(xAxis.axisPointer || {}, {
      label: {
        margin: -offset - gridBoundingRect.height
      }
    });
  }

  protected _logAxisHandle(axis: string, axisComponent: Option['xAxis'] | Option['yAxis']) {
    const axisName = `${axis}Axis`;
    if (axisComponent.min > 1) {
      return;
    }

    const offset = Math.abs(1 - axisComponent.min);
    this._common[`${axis}Offset`] = offset;
    axisComponent.min += offset;
    axisComponent.max += offset;

    if (!axisComponent.axisLabel) {
      axisComponent.axisLabel = {};
    }
    const userAxisFormatter = axisComponent.axisLabel.formatter;
    axisComponent.axisLabel.formatter = (value: number, index: number) => {
      if (userAxisFormatter) {
        return userAxisFormatter(value - offset, index);
      }
      return value - offset;
    };
  }

  protected _autoLabelLayout(): void {
    if (!this._option.series.labelLayout || !this._option.series.labelLayout.dvAutoLayout) {
      return;
    }
    const { min, max } = this._option.yAxis;
    const avg = (max + min) / 2;

    this._option.series.data.forEach(d => {
      if (!d || !d.value) {
        return;
      }
      d.label = merge({ position: d.value[1] > avg ? 'bottom' : 'top' }, d.label || {});
    });
  }

  protected _renderItemHandle(): void {
    this._option.series.renderItem = (params, api) => {
      const { seriesIndex, seriesName, seriesId, dataIndex } = params;
      const data = this._data[dataIndex];
      if (!isValidData(data)) {
        return;
      }
      const dataName = data.name;
      const dataId = data.id;
      // get symbol type
      const { dvSymbol } = this._option.series;
      // get graphic
      const graphic = graphicHandle[dvSymbol](this._option, data, {
        scale: {
          xScale: this._getXScale(),
          yScale: this._getYScale(),
          rScale: this._getRScale()
        },
        api
      });

      let seriesMark = `seriesIndex${seriesIndex}`;
      seriesId && (seriesMark = `seriesId${seriesId}`);
      seriesName && (seriesMark = `seriesName${seriesName}`);
      let dataMark = `dataIndex${dataIndex}`;
      dataId && (dataMark = `dataId${dataId}`);
      dataName && (dataMark = `dataName${dataName}`);

      graphic.id = `${seriesMark}-${dataMark}`;

      return graphic;
    };
  }

  protected _getRScale() {
    const { dvSymbolSize, dvRValueRange } = this._option.series;
    const rSizeRange = Array.isArray(dvSymbolSize) ? dvSymbolSize : [dvSymbolSize, dvSymbolSize];
    return d3Scale.scaleLinear().domain(dvRValueRange).range(rSizeRange);
  }

  protected _getXScale() {
    const dom = this._chart.getDom();
    const gridBoundingRect = getCoordBoundingRect(dom as HTMLDivElement, this._option.grid || {});
    const xAxisComponent = this._option.xAxis;
    const xPosRange = [gridBoundingRect.x, gridBoundingRect.x + gridBoundingRect.width];
    const xValRange = [this._option.xAxis.min, this._option.xAxis.max];
    let scale = axisTypeScaleMap[this._option.xAxis.type]();
    if (xAxisComponent.type === 'log') {
      const { logBase = 10 } = xAxisComponent;
      scale = scale.base(logBase);
    }
    return scale.domain(xValRange).range(xPosRange);
  }

  protected _getYScale() {
    const dom = this._chart.getDom();
    const gridBoundingRect = getCoordBoundingRect(dom as HTMLDivElement, this._option.grid || {});
    const yAxisComponent = this._option.yAxis;
    const yPosRange = [gridBoundingRect.y, gridBoundingRect.y + gridBoundingRect.height];
    const yValRange = [this._option.yAxis.min, this._option.yAxis.max];
    let scale = axisTypeScaleMap[this._option.yAxis.type]();
    if (yAxisComponent.type === 'log') {
      const { logBase = 10 } = yAxisComponent;
      scale = scale.base(logBase);
    }
    return scale.domain(yValRange).range(yPosRange);
  }
}
